import React, { useContext, useMemo } from "react";
import { useSelector } from "react-redux";
import { UserContext } from "../../UserContext";
import { openPopup, closePopup } from "./popupUIStore";

function normalizeRole(value) {
   return String(value || "")
      .trim()
      .toUpperCase();
}

function pickUserByRole(users, roles = []) {
   const set = new Set(roles.map((role) => normalizeRole(role)));
   return (users || []).find((entry) => set.has(normalizeRole(entry?.role))) || null;
}

function toDateInputValue(isoLike) {
   const parsed = new Date(isoLike);
   if (Number.isNaN(parsed.getTime())) {
      const fallback = new Date();
      return fallback.toISOString().slice(0, 10);
   }
   return parsed.toISOString().slice(0, 10);
}

function toTimeInputValue(isoLike) {
   const parsed = new Date(isoLike);
   if (Number.isNaN(parsed.getTime())) return "09:00";
   const hh = String(parsed.getHours()).padStart(2, "0");
   const mm = String(parsed.getMinutes()).padStart(2, "0");
   return `${hh}:${mm}`;
}

function plusMinutesISO(baseIso, minutes) {
   const parsed = new Date(baseIso);
   if (Number.isNaN(parsed.getTime())) return new Date().toISOString();
   return new Date(parsed.getTime() + Number(minutes || 0) * 60000).toISOString();
}

export default function InternalPopupLauncher() {
   const { user } = useContext(UserContext) || {};
   const students = useSelector((state) => state.students?.list || []);
   const instructors = useSelector((state) => state.instructors?.list || []);
   const users = useSelector((state) => state.users?.list || []);
   const reservations = useSelector((state) => state.reservations?.list || []);

   const sampleStudent = useMemo(() => {
      const fromStore = (students || [])[0];
      if (fromStore) return fromStore;
      return {
         id: 1,
         firstName: "Student",
         lastName: "Demo",
         email: "student.demo@local",
      };
   }, [students]);

   const sampleInstructor = useMemo(() => {
      const fromStore = (instructors || [])[0];
      if (fromStore) return fromStore;
      return {
         id: 1,
         userId: 1,
         firstName: "Instructor",
         lastName: "Demo",
         sector: "Botanica",
         instructorType: "SUPERIOR",
      };
   }, [instructors]);

   const sampleManager = useMemo(() => {
      const fromStore = pickUserByRole(users, ["MANAGER", "ADMIN"]);
      if (fromStore) return fromStore;
      if (normalizeRole(user?.role) === "MANAGER" || normalizeRole(user?.role) === "ADMIN") {
         return {
            ...user,
            role: normalizeRole(user?.role) || "MANAGER",
         };
      }
      return {
         id: 2,
         firstName: "Manager",
         lastName: "Demo",
         email: "manager.demo@local",
         role: "MANAGER",
      };
   }, [user, users]);

   const sampleProfessor = useMemo(() => {
      const fromStore = pickUserByRole(users, ["PROFESSOR"]);
      if (fromStore) return fromStore;
      if (normalizeRole(user?.role) === "PROFESSOR") {
         return {
            ...user,
            role: "PROFESSOR",
         };
      }
      return {
         id: 3,
         firstName: "Profesor",
         lastName: "Demo",
         email: "profesor.demo@local",
         role: "PROFESSOR",
      };
   }, [user, users]);

   const sampleReservation = useMemo(() => {
      const fromStore = (reservations || [])[0];
      if (fromStore) return fromStore;

      const nowIso = new Date().toISOString();
      return {
         id: 1,
         reservationId: 1,
         userId: sampleStudent?.id || 1,
         studentId: sampleStudent?.id || 1,
         instructorId: sampleInstructor?.id || 1,
         startTime: nowIso,
         endTime: plusMinutesISO(nowIso, 90),
         sector: sampleInstructor?.sector || "Botanica",
         gearbox: "Manual",
         color: "--black-t",
         isFavorite: false,
         isImportant: false,
         privateMessage: "",
         student: sampleStudent || null,
         instructor: sampleInstructor || null,
      };
   }, [reservations, sampleInstructor, sampleStudent]);

   const reservationIdForPopup = useMemo(() => {
      const candidate =
         sampleReservation?.id ??
         sampleReservation?.reservationId ??
         sampleReservation?.reservation_id ??
         null;
      return candidate != null ? String(candidate) : "";
   }, [sampleReservation]);

   const sampleStartIso = useMemo(() => {
      const value =
         sampleReservation?.startTime ??
         sampleReservation?.start ??
         sampleReservation?.startAt ??
         "";
      const text = String(value || "").trim();
      return text || new Date().toISOString();
   }, [sampleReservation]);

   const sampleEndIso = useMemo(() => {
      const value =
         sampleReservation?.endTime ??
         sampleReservation?.end ??
         sampleReservation?.endAt ??
         "";
      const text = String(value || "").trim();
      if (text) return text;
      return plusMinutesISO(sampleStartIso, 90);
   }, [sampleReservation, sampleStartIso]);

   const sampleEvent = useMemo(
      () => ({
         start: sampleStartIso,
         end: sampleEndIso,
         gearbox: sampleReservation?.gearbox || "Manual",
         sector: sampleReservation?.sector || "Botanica",
      }),
      [sampleEndIso, sampleReservation?.gearbox, sampleReservation?.sector, sampleStartIso],
   );

   const sampleInstructorName = useMemo(
      () =>
         `${sampleInstructor?.firstName || ""} ${sampleInstructor?.lastName || ""}`.trim() ||
         "Instructor Demo",
      [sampleInstructor],
   );

   const sampleInstructorOptions = useMemo(
      () => [
         {
            value: String(sampleInstructor?.id || 1),
            label: sampleInstructorName,
            sector: sampleInstructor?.sector || "Botanica",
            gearbox: sampleInstructor?.gearbox || "manual",
            raw: sampleInstructor,
         },
      ],
      [sampleInstructor, sampleInstructorName],
   );

   const samplePracticalExamSlot = useMemo(
      () => ({
         id: "preview-slot",
         dateKey: toDateInputValue(sampleStartIso),
         timeKey: toTimeInputValue(sampleStartIso),
         sector: sampleInstructor?.sector || "Botanica",
         gearbox: "manual",
         capacity: 2,
         instructorId: sampleInstructor?.id || 1,
         instructorName: sampleInstructorName,
         isActive: true,
         raw: {},
      }),
      [sampleInstructor?.id, sampleInstructor?.sector, sampleInstructorName, sampleStartIso],
   );

   const samplePracticalExamReservation = useMemo(
      () => ({
         id: "preview-reservation",
         status: "SCHEDULED",
         color: "--event-default",
         position: 1,
         student: sampleStudent,
         user: sampleStudent,
         slot: samplePracticalExamSlot,
         raw: {
            student: sampleStudent,
            slot: samplePracticalExamSlot,
         },
      }),
      [samplePracticalExamSlot, sampleStudent],
   );

   const popupButtons = useMemo(
      () => [
         {
            type: "studentDetails",
            label: "Detalii student",
            props: { student: sampleStudent },
         },
         {
            type: "studentProfile",
            label: "Profil student",
            props: {},
         },
         {
            type: "eventInfo",
            label: "Detalii lectie",
            props: { event: sampleEvent },
         },
         {
            type: "studentReservations",
            label: "Programari student",
            props: {},
         },
         {
            type: "ultraSlotHistory",
            label: "Istoric slot Ultra",
            props: {
               instructorId: String(sampleInstructor?.id || ""),
               instructorName: sampleInstructorName,
               slotLabel: "Slot demo",
               slotStartISO: sampleStartIso,
            },
         },
         {
            type: "instructorStudents",
            label: "Profil student pentru instructor",
            props: { initialStudent: sampleStudent },
         },
         {
            type: "reservationEdit",
            label: "Editare rezervare",
            props: {
               reservationId: reservationIdForPopup,
               reservationSnapshot: sampleReservation,
            },
         },
         {
            type: "createRezervation",
            label: "Creare rezervare",
            props: {
               initialDate: toDateInputValue(sampleStartIso),
               initialTime: toTimeInputValue(sampleStartIso),
               initialStudentId: String(sampleStudent?.id || ""),
               initialInstructorId: String(sampleInstructor?.id || ""),
            },
         },
         {
            type: "instructorDetails",
            label: "Profil instructor",
            props: { instructor: sampleInstructor },
         },
         {
            type: "managerAdminDetails",
            label: "Profil manager/admin",
            props: { user: sampleManager },
         },
         {
            type: "professorDetails",
            label: "Profil profesor",
            props: { user: sampleProfessor },
         },
         {
            type: "staffDetails",
            label: "Staff details",
            props: { user: sampleManager },
         },
         {
            type: "personalCreate",
            label: "Creare personal",
            props: {},
         },
         {
            type: "studentVideoLibrary",
            label: "Biblioteca video",
            props: {},
         },
         {
            type: "questionCategories",
            label: "Categorii intrebari",
            props: {},
         },
         {
            type: "startExam",
            label: "Start examen",
            props: {},
         },
         {
            type: "sAddProg",
            label: "Programare student",
            props: {},
         },
         {
            type: "practicalExamBooking",
            label: "Programare Ex. practic student",
            props: {},
         },
         {
            type: "practicalExamSlot",
            label: "Ex. practic: creare slot",
            props: {
               mode: "create",
               initialValues: {
                  date: samplePracticalExamSlot.dateKey,
                  time: samplePracticalExamSlot.timeKey,
                  sector: samplePracticalExamSlot.sector,
                  gearbox: samplePracticalExamSlot.gearbox,
                  capacity: samplePracticalExamSlot.capacity,
                  instructorId: samplePracticalExamSlot.instructorId,
               },
               instructorOptions: sampleInstructorOptions,
            },
         },
         {
            type: "practicalExamSlot",
            label: "Ex. practic: editare slot",
            props: {
               mode: "edit",
               slot: samplePracticalExamSlot,
               instructorOptions: sampleInstructorOptions,
            },
         },
         {
            type: "practicalExamGenerateSlots",
            label: "Ex. practic: generare sloturi",
            props: {
               monthKey: samplePracticalExamSlot.dateKey.slice(0, 7),
               initialValues: {
                  startDate: samplePracticalExamSlot.dateKey,
                  endDate: samplePracticalExamSlot.dateKey,
                  sector: samplePracticalExamSlot.sector,
                  gearbox: samplePracticalExamSlot.gearbox,
                  capacity: samplePracticalExamSlot.capacity,
                  instructorId: samplePracticalExamSlot.instructorId,
               },
               instructorOptions: sampleInstructorOptions,
            },
         },
         {
            type: "practicalExamBookStudent",
            label: "Ex. practic: creare rezervare",
            props: {
               slot: samplePracticalExamSlot,
               seatNumber: 1,
            },
         },
         {
            type: "practicalExamBookStudent",
            label: "Ex. practic: editare rezervare",
            props: {
               slot: samplePracticalExamSlot,
               seatNumber: 1,
               reservation: samplePracticalExamReservation,
            },
         },
      ],
      [
         reservationIdForPopup,
         sampleEvent,
         sampleInstructor,
         sampleInstructorName,
         sampleInstructorOptions,
         sampleManager,
         samplePracticalExamReservation,
         samplePracticalExamSlot,
         sampleProfessor,
         sampleReservation,
         sampleStartIso,
         sampleStudent,
      ],
   );

   return (
      <section className="internalPopupLauncher">
         <div className="internalPopupLauncher__head">
            <div className="internalPopupLauncher__titleBlock">
               <h2 className="internalPopupLauncher__title">Interne: launcher popup-uri</h2>
               <p className="internalPopupLauncher__subtitle">
                  Deschide rapid toate popup-urile din aplicatie pentru verificare UI.
               </p>
            </div>

            <button
               type="button"
               className="internalPopupLauncher__closeBtn"
               onClick={() => closePopup()}
            >
               Inchide popup activ
            </button>
         </div>

         <div className="internalPopupLauncher__grid">
            {popupButtons.map((entry) => (
               <button
                  key={`${entry.type}-${entry.label}`}
                  type="button"
                  className="internalPopupLauncher__item"
                  onClick={() => openPopup(entry.type, entry.props)}
               >
                  <span className="internalPopupLauncher__itemLabel">{entry.label}</span>
                  <span className="internalPopupLauncher__itemType">{entry.type}</span>
               </button>
            ))}
         </div>
      </section>
   );
}
